<script lang="ts" setup>
import type { ComponentPublicInstance } from 'vue';

const CODE_LENGTH = 4;

const props = defineProps<{
  failed?: boolean;
  dark?: boolean;
  readonly?: boolean;
  dataTestId?: string;
}>();
const emit = defineEmits<{
  (event: 'complete', value: string): void;
  (event: 'input'): void;
}>();

const code = ref<string[]>([]);

const inputs = ref<ComponentPublicInstance[]>([]);

const onComplete = (value: string[]) => emit('complete', value.join(''));

watch(
  () => code.value.length,
  (v) => {
    if (v) emit('input');
  },
  { deep: true },
);

watch(
  () => props.failed,
  (v) => {
    if (v) {
      code.value = [];
      window.navigator.vibrate?.([200]);

      setTimeout(() => {
        inputs.value[0].$el.focus();
      });
    }
  },
);
</script>

<template>
  <PinInputRoot
    v-model="code"
    @complete="onComplete"
    :disabled="readonly"
    otp
    required
    type="number"
    class="phoneCode"
    :class="{ phoneCode_failed: failed, phoneCode_dark: dark }"
    :data-test-id="dataTestId"
  >
    <PinInputInput
      v-for="(id, index) in CODE_LENGTH"
      :key="id"
      ref="inputs"
      :index="index"
      :autofocus="index === 0"
      class="phoneCode__input"
      :data-test-id="dataTestId && `${dataTestId}-symbol`"
    />
  </PinInputRoot>
</template>

<style lang="scss">
.phoneCode {
  $self: &;

  display: flex;
  gap: 25px;

  @include mq('sm') {
    gap: 18px;
  }

  &__input {
    width: 40px;
    height: 40px;
    border: 1px solid #c0c0c0;
    border-radius: 4px;
    font-size: 14px;
    color: #121212;
    padding: 0;
    background-color: transparent;
    outline: none;
    text-align: center;
    transition:
      border-color 0.3s ease,
      transform 0.3s ease;
    caret-color: transparent;
    -moz-appearance: textfield;

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }

    &:valid {
      border-color: #121212;
    }

    &:focus {
      animation: phone-code-input-focused 1.5s ease-in-out infinite;
    }

    &:read-only {
      pointer-events: none;
    }

    @include mq('sm') {
      width: 58px;
      height: 58px;
      font-size: 20px;
    }
  }

  &_dark &__input {
    color: #ffffff;

    &:valid {
      border-color: #ffffff;
    }
  }

  &#{$self}_failed {
    animation: phone-code-failed 0.3s ease;

    #{$self}__input {
      border-color: #eb5757;
    }
  }

  @keyframes phone-code-input-focused {
    0% {
      transform: translateY(0);
    }

    50% {
      transform: translateY(-4px);
    }

    100% {
      transform: translateY(0);
    }
  }

  @keyframes phone-code-failed {
    0% {
      transform: translateX(0);
    }

    25% {
      transform: translateX(-8px);
    }

    75% {
      transform: translateX(8px);
    }

    100% {
      transform: translateX(0);
    }
  }
}
</style>
